import { useState, useMemo, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  FileText,
  User,
  Clock,
  Calendar,
  AlertCircle,
  RefreshCw,
  XCircle,
  PanelRightOpen,
  PanelRightClose,
  ChevronDown,
  ChevronRight,
  Sparkles,
  MessageSquare,
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { useContentTask, useContentVersions } from '../../hooks/useContentTasks'
import { usePersonas } from '../../hooks/usePersonas'
import { useContentTasks } from '../../hooks/useContentTasks'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { StatusBadge } from '../ui/StatusBadge'
import { MarkdownRenderer } from './MarkdownRenderer'
import { ContentTabs } from './ContentTabs'
import { ContentToolbar } from './ContentToolbar'
import { StatusPipeline } from './StatusPipeline'
import { CritiquePanel } from './CritiquePanel'
import { VersionCompare } from './VersionCompare'
import { FeedbackForm } from './FeedbackForm'
import { SEOPreview } from './SEOPreview'
import { SocialPreview } from './SocialPreview'
import { cleanAIContent, isExcerptDuplicate, extractHeadings, extractCitationNumbers, countWords, parseStructuredBody } from '../../lib/content-utils'
import { validateContentVersionPayload } from '../../lib/content-schema'
import { editContentWithAI } from '../../lib/api'
import { ContentTableOfContents } from './ContentTableOfContents'
import { GenerationSteps } from './GenerationSteps'
import { SourcesList } from './SourcesList'
import { ContentImagesPanel } from './ContentImagesPanel'
import { toast } from 'sonner'

export function ContentDetailPage() {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [activePieceId, setActivePieceId] = useState<string | null>(null)
  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [readingMode, setReadingMode] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editDraft, setEditDraft] = useState<{ body: string; title: string; excerpt: string; cta: string }>({
    body: '',
    title: '',
    excerpt: '',
    cta: '',
  })
  const [aiEditPanelOpen, setAiEditPanelOpen] = useState(false)
  const [aiEditInstruction, setAiEditInstruction] = useState('')
  const [aiEditSelection, setAiEditSelection] = useState<string | null>(null)
  const [aiEditLoading, setAiEditLoading] = useState(false)
  const [aiEditPreview, setAiEditPreview] = useState<{ full_body: string } | null>(null)
  const [floatingSelection, setFloatingSelection] = useState<{ text: string; rect: { top: number; left: number; width: number; height: number } } | null>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const aiEditTooltipRef = useRef<HTMLDivElement>(null)

  const { data: taskData, isLoading } = useContentTask(taskId || null)
  const { data: personas } = usePersonas()
  const { rerunTask, updateContentStatus, cancelTask, saveContentVersion, isSavingVersion } = useContentTasks()

  const task = taskData?.task
  const pieces = taskData?.pieces

  // Set active piece to first one when data loads
  const activePiece = useMemo(() => {
    if (!pieces || pieces.length === 0) return null
    if (activePieceId) {
      const found = pieces.find((p) => p.id === activePieceId)
      if (found) return found
    }
    return pieces[0]
  }, [pieces, activePieceId])

  // Auto-select first piece
  useMemo(() => {
    if (pieces && pieces.length > 0 && !activePieceId) {
      setActivePieceId(pieces[0].id)
    }
  }, [pieces, activePieceId])

  // Fetch versions for active piece
  const { data: versions, isLoading: isLoadingVersions } = useContentVersions(activePiece?.id || null)

  // Get the latest version's critique data
  const latestVersion = useMemo(() => {
    if (!versions || versions.length === 0) return null
    return versions.reduce((latest, v) => (v.version > latest.version ? v : latest), versions[0])
  }, [versions])

  const personaName = useMemo(() => {
    if (!task || !personas) return 'Desconocido'
    return personas.find((p) => p.id === task.persona_id)?.name || 'Desconocido'
  }, [task, personas])

  /** Normalized title/excerpt/body/cta for display. When body is stored as JSON, parse and use inner fields. */
  const displayFields = useMemo(() => {
    if (!activePiece) return null
    const structured = parseStructuredBody(activePiece.body)
    if (structured) {
      return {
        title: structured.title ?? activePiece.title ?? null,
        excerpt: structured.excerpt ?? activePiece.excerpt ?? null,
        body: structured.body,
        cta: structured.cta ?? activePiece.cta ?? null,
      }
    }
    return {
      title: activePiece.title ?? null,
      excerpt: activePiece.excerpt ?? null,
      body: activePiece.body ?? '',
      cta: activePiece.cta ?? null,
    }
  }, [activePiece])

  const tocHeadings = useMemo(
    () => extractHeadings(displayFields?.body ?? activePiece?.body ?? ''),
    [displayFields?.body, activePiece?.body]
  )

  const citationRefsWhenNoSources = useMemo(() => {
    const body = displayFields?.body ?? activePiece?.body
    if (!body || (activePiece?.sources && activePiece.sources.length > 0)) return []
    return extractCitationNumbers(body)
  }, [displayFields?.body, activePiece?.body, activePiece?.sources])

  // Sync edit draft when active piece changes (use normalized display fields so JSON body is editable as plain text)
  useEffect(() => {
    if (activePiece && displayFields) {
      setEditDraft({
        body: displayFields.body,
        title: displayFields.title ?? '',
        excerpt: displayFields.excerpt ?? '',
        cta: displayFields.cta ?? '',
      })
      setAiEditSelection(null)
      setAiEditPreview(null)
    }
  }, [activePiece?.id, displayFields])

  // When entering edit mode, ensure draft is current (from display fields)
  useEffect(() => {
    if (isEditMode && activePiece && displayFields) {
      setEditDraft({
        body: displayFields.body,
        title: displayFields.title ?? '',
        excerpt: displayFields.excerpt ?? '',
        cta: displayFields.cta ?? '',
      })
    }
  }, [isEditMode, activePiece, displayFields])
  const canRerun = task?.status === 'failed' || task?.status === 'cancelled'
  const canCancel = task?.status === 'pending' || task?.status === 'running'
  const canEdit = Boolean(activePiece && task?.organization_id && taskId)
  const hasError = task?.status === 'failed' && task?.error_message

  const handleRerun = async () => {
    if (taskId && window.confirm('¿Quieres reintentar esta tarea?')) {
      await rerunTask(taskId)
    }
  }

  const handleCancel = async () => {
    if (taskId && window.confirm('¿Estás seguro de que quieres cancelar esta tarea?')) {
      await cancelTask(taskId)
    }
  }

  const handleUpdateStatus = async (newStatus: string) => {
    if (activePiece) {
      await updateContentStatus(activePiece.id, newStatus)
    }
  }

  const handleEnterEditMode = () => {
    if (activePiece && displayFields) {
      setEditDraft({
        body: displayFields.body,
        title: displayFields.title ?? '',
        excerpt: displayFields.excerpt ?? '',
        cta: displayFields.cta ?? '',
      })
      setIsEditMode(true)
    }
  }

  const handleExitEditMode = () => {
    setIsEditMode(false)
  }

  const handleSaveVersion = async () => {
    if (!activePiece || !taskId || !task?.organization_id) return
    const validation = validateContentVersionPayload({
      body: editDraft.body,
      title: editDraft.title || null,
      excerpt: editDraft.excerpt || null,
      cta: editDraft.cta || null,
    })
    if (!validation.success) {
      const firstError = Object.values(validation.errors)[0]
      toast.error('Error de validación', { description: firstError })
      return
    }
    await saveContentVersion(activePiece.id, taskId, validation.data)
    setIsEditMode(false)
  }

  const handleAiEditApply = async () => {
    if (!activePiece || !task?.organization_id || !aiEditInstruction.trim()) return
    setAiEditLoading(true)
    setAiEditPreview(null)
    try {
      const res = await editContentWithAI(activePiece.id, aiEditInstruction.trim(), {
        selection: aiEditSelection ?? undefined,
        context: aiEditSelection ? 'selection_only' : 'full',
      })
      setAiEditPreview({ full_body: res.full_body })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al editar con IA'
      toast.error('Edición con IA', { description: msg })
    } finally {
      setAiEditLoading(false)
    }
  }

  const handleAiEditAccept = async () => {
    if (!activePiece || !taskId || !aiEditPreview) return
    try {
      await saveContentVersion(activePiece.id, taskId, {
        body: aiEditPreview.full_body,
        edited_with_ai: true,
      })
      setEditDraft((d) => ({ ...d, body: aiEditPreview.full_body }))
      setAiEditPreview(null)
      setAiEditInstruction('')
      setAiEditSelection(null)
    } catch {
      toast.error('Error al guardar la versión')
    }
  }

  const handleAiEditReject = () => {
    setAiEditPreview(null)
  }

  const handleContentMouseUp = () => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setFloatingSelection(null)
      return
    }
    const text = sel.toString().trim()
    if (!text) {
      setFloatingSelection(null)
      return
    }
    if (!contentRef.current || !contentRef.current.contains(sel.anchorNode)) {
      return
    }
    const range = sel.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    setFloatingSelection({
      text,
      rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
    })
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        contentRef.current?.contains(target) ||
        aiEditTooltipRef.current?.contains(target)
      ) {
        return
      }
      setFloatingSelection(null)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!task) {
    return (
      <div className="p-8">
        <button
          onClick={() => navigate('/content')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a Contenido
        </button>
        <div className="text-center py-16 text-slate-500">
          <XCircle className="w-12 h-12 mx-auto mb-3 text-slate-400" />
          <p>No se encontró la tarea.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Top bar: breadcrumb + task metadata (hidden in print) */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 print:hidden">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate('/content')}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Contenido
          </button>
          <div className="flex items-center gap-2">
            {canCancel && (
              <button onClick={handleCancel} className="btn btn-secondary flex items-center gap-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50">
                <XCircle className="w-4 h-4" />
                Cancelar
              </button>
            )}
            {canRerun && (
              <button onClick={handleRerun} className="btn btn-secondary flex items-center gap-2 text-sm">
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            )}
            <button
              onClick={() => setRightPanelOpen(!rightPanelOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg text-slate-500 transition-colors"
              title={rightPanelOpen ? 'Cerrar panel' : 'Abrir panel'}
            >
              {rightPanelOpen ? (
                <PanelRightClose className="w-5 h-5" />
              ) : (
                <PanelRightOpen className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl font-bold text-slate-800 truncate">{task.topic}</h1>
              <StatusBadge status={task.status} />
            </div>
            {task.brief && (
              <p className="text-sm text-slate-500 line-clamp-1">{task.brief}</p>
            )}
          </div>
        </div>

        {/* Task metadata row */}
        <div className="flex items-center gap-5 mt-3 text-sm text-slate-500">
          <span className="flex items-center gap-1.5">
            <FileText className="w-4 h-4" />
            {task.content_type}
          </span>
          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
            {task.channel}
          </span>
          {personaName && (
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {personaName}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            {formatDistanceToNow(new Date(task.created_at), { addSuffix: true, locale: es })}
          </span>
          {task.conversation_id && (
            <Link
              to={`/chat/${task.conversation_id}`}
              className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 hover:underline"
            >
              <MessageSquare className="w-4 h-4" />
              Ver conversacion origen
            </Link>
          )}
          {task.completed_at && (
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              Completada{' '}
              {formatDistanceToNow(new Date(task.completed_at), { addSuffix: true, locale: es })}
            </span>
          )}
        </div>

        {/* Keywords */}
        {task.keywords && task.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {task.keywords.map((kw, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs"
              >
                {kw}
              </span>
            ))}
          </div>
        )}

        {/* Error message */}
        {hasError && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error en la generación</p>
                <p className="text-xs text-red-700 mt-0.5">{task.error_message}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content area */}
      {pieces && pieces.length > 0 ? (
        <>
          {/* Content tabs (hidden in print) */}
          <div className="print:hidden">
            <ContentTabs
              pieces={pieces}
              activeId={activePiece?.id || null}
              onSelect={setActivePieceId}
            />
          </div>

          {/* Main content layout: content + right panel */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            {/* Center: Content area - min-h-0 so overflow-y-auto scrolls inside flex */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {activePiece && (
                <>
                  {/* Print-only minimal header (title, author, date) */}
                  <div className="hidden print:block px-6 pt-6 pb-4 border-b border-slate-200">
                    <h1 className="text-xl font-bold text-slate-900">
                      {displayFields?.title || activePiece.title || task.topic}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                      {personaName}
                      {task.created_at && (
                        <>
                          {' · '}
                          {format(new Date(task.created_at), 'd MMM yyyy', { locale: es })}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Status pipeline (hidden in print) */}
                  <div className="px-6 py-4 bg-white border-b border-slate-200 print:hidden">
                    <StatusPipeline
                      currentStatus={activePiece.status || 'draft'}
                      onStatusChange={handleUpdateStatus}
                    />
                  </div>

                  {/* Toolbar (hidden in print) */}
                  <div className="print:hidden">
                    <ContentToolbar
                      body={isEditMode ? editDraft.body : (displayFields?.body ?? activePiece.body)}
                      sources={isEditMode ? undefined : activePiece.sources}
                      wordCount={isEditMode ? countWords(editDraft.body) : activePiece.word_count}
                      currentVersion={activePiece.current_version}
                      readingMode={readingMode}
                      onToggleReadingMode={() => setReadingMode((m) => !m)}
                      isEditMode={isEditMode}
                      canEdit={canEdit}
                      onEnterEditMode={handleEnterEditMode}
                      onExitEditMode={handleExitEditMode}
                      onSaveVersion={handleSaveVersion}
                      isSavingVersion={isSavingVersion}
                    />
                  </div>

                  {/* Editar con IA - collapsible panel below toolbar */}
                  <div className="print:hidden border-b border-slate-200 bg-slate-50/50">
                    <button
                      type="button"
                      onClick={() => setAiEditPanelOpen((o) => !o)}
                      className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      {aiEditPanelOpen ? (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-500" />
                      )}
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      Editar con IA
                    </button>
                    {aiEditPanelOpen && (
                      <div className="px-4 pb-4 space-y-3">
                        <p className="text-xs text-slate-500">
                          Indica qué quieres cambiar; la IA aplicará la edición. Opcional: selecciona texto en el cuerpo para editar solo esa parte.
                        </p>
                        {aiEditSelection && (
                          <div className="rounded border border-amber-200 bg-amber-50/80 px-3 py-2">
                            <p className="text-xs font-medium text-amber-800 mb-1">Editar selección</p>
                            <p className="text-sm text-slate-700 line-clamp-2 break-words">
                              {aiEditSelection.length > 200 ? `${aiEditSelection.slice(0, 200)}...` : aiEditSelection}
                            </p>
                            <button
                              type="button"
                              onClick={() => setAiEditSelection(null)}
                              className="mt-1 text-xs text-amber-700 hover:underline"
                            >
                              Quitar selección (editar todo el cuerpo)
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={aiEditInstruction}
                            onChange={(e) => setAiEditInstruction(e.target.value)}
                            placeholder="Ej: acortar el primer párrafo, tono más formal..."
                            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                            disabled={aiEditLoading}
                          />
                          <button
                            type="button"
                            disabled={aiEditLoading || !aiEditInstruction.trim()}
                            onClick={handleAiEditApply}
                            className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {aiEditLoading ? 'Aplicando...' : 'Aplicar'}
                          </button>
                        </div>
                        {aiEditPreview && (
                          <div className="rounded border border-slate-200 bg-white p-3 space-y-3">
                            <p className="text-xs font-semibold text-slate-600">Vista previa del cambio</p>
                            <div className="max-h-48 overflow-y-auto rounded bg-slate-50 p-2 text-sm text-slate-800 whitespace-pre-wrap font-mono">
                              {aiEditPreview.full_body}
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={handleAiEditAccept}
                                disabled={isSavingVersion}
                                className="px-3 py-1.5 rounded text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                Aceptar
                              </button>
                              <button
                                type="button"
                                onClick={handleAiEditReject}
                                className="px-3 py-1.5 rounded text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-100"
                              >
                                Rechazar
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content: constrained width for readability; toolbar and pipeline stay full width */}
                  <div
                    ref={contentRef}
                    onMouseUp={handleContentMouseUp}
                    className="px-6 py-6 max-w-[65ch] mx-auto"
                  >
                    {isEditMode ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Titulo</label>
                          <input
                            type="text"
                            value={editDraft.title}
                            onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Resumen (excerpt)</label>
                          <textarea
                            value={editDraft.excerpt}
                            onChange={(e) => setEditDraft((d) => ({ ...d, excerpt: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Cuerpo (Markdown)</label>
                          <textarea
                            value={editDraft.body}
                            onChange={(e) => setEditDraft((d) => ({ ...d, body: e.target.value }))}
                            rows={16}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">CTA</label>
                          <input
                            type="text"
                            value={editDraft.cta}
                            onChange={(e) => setEditDraft((d) => ({ ...d, cta: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                    {/* Title */}
                    {displayFields?.title && (
                      <h2 className="text-2xl font-bold text-slate-900 mb-4">
                        {displayFields.title}
                      </h2>
                    )}

                    {/* Excerpt (only if not duplicate of body) */}
                    {displayFields?.excerpt &&
                      !isExcerptDuplicate(displayFields.excerpt, displayFields.body) && (
                        <div className="mb-6 p-4 bg-slate-50 rounded-lg border-l-4 border-emerald-500">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                            Resumen
                          </p>
                          <p className="text-sm text-slate-600 italic leading-relaxed">
                            {cleanAIContent(displayFields.excerpt)}
                          </p>
                        </div>
                      )}

                    {/* Body - Markdown rendered */}
                    <MarkdownRenderer content={displayFields?.body ?? activePiece.body} sources={activePiece?.sources} />

                    {/* CTA */}
                    {displayFields?.cta && (
                      <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <p className="text-sm font-medium text-emerald-900 mb-1">
                          Call to Action
                        </p>
                        <p className="text-sm text-emerald-700">{displayFields.cta}</p>
                      </div>
                    )}
                    </>
                    )}

                    {/* Resumen de investigación */}
                    {activePiece.research_summary && (
                      <section className="mt-6 p-4 bg-slate-50 rounded-lg border-l-4 border-indigo-500" aria-label="Resumen de investigación">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                          Resumen de investigación
                        </h3>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {activePiece.research_summary}
                        </p>
                        {activePiece.research_key_findings && activePiece.research_key_findings.length > 0 && (
                          <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-slate-600">
                            {activePiece.research_key_findings.map((finding, i) => (
                              <li key={i}>{finding}</li>
                            ))}
                          </ul>
                        )}
                      </section>
                    )}

                    {/* Fuentes: in main flow for reading mode and print */}
                    {activePiece.sources && activePiece.sources.length > 0 && (
                      <SourcesList sources={activePiece.sources} />
                    )}
                    {/* Placeholder Referencias when body has [N] but no sources (so [N] links scroll) */}
                    {citationRefsWhenNoSources.length > 0 && (
                      <section className="mt-6 pt-6 border-t border-slate-200" aria-label="Referencias">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                          Referencias citadas en el texto
                        </h3>
                        <p className="text-sm text-slate-500 mb-3">
                          Las fuentes no están disponibles para esta pieza. Los enlaces [N] llevan a esta lista.
                        </p>
                        <ol className="list-decimal pl-6 space-y-2 text-sm text-slate-600">
                          {citationRefsWhenNoSources.map((n) => (
                            <li key={n} id={`fuente-${n}`} className="leading-relaxed">
                              Referencia {n}
                            </li>
                          ))}
                        </ol>
                      </section>
                    )}
                  </div>
                  {floatingSelection && (
                    <div
                      ref={aiEditTooltipRef}
                      style={{
                        position: 'fixed',
                        top: floatingSelection.rect.top + floatingSelection.rect.height + 6,
                        left: floatingSelection.rect.left,
                        zIndex: 50,
                      }}
                      className="rounded-lg border border-slate-200 bg-white shadow-lg px-3 py-2 flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <button
                        type="button"
                        onClick={() => {
                          setAiEditSelection(floatingSelection.text)
                          setAiEditPanelOpen(true)
                          setFloatingSelection(null)
                        }}
                        className="text-sm font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                      >
                        Editar con IA
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right panel: versions, critique, feedback, SEO, social (hidden in reading mode and print) */}
            {rightPanelOpen && !readingMode && (
              <div className="w-80 border-l border-slate-200 bg-white overflow-y-auto flex-shrink-0 print:hidden">
                <div className="p-4 space-y-6">
                  {/* Fuentes + Resumen de investigación: destacados arriba cuando hay datos */}
                  {(activePiece?.sources && activePiece.sources.length > 0) || activePiece?.research_summary ? (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-4">
                      <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">
                        Fuentes y contexto
                      </span>
                      {activePiece?.sources && activePiece.sources.length > 0 && (
                        <div className="border-t border-indigo-100 pt-3 first:border-t-0 first:pt-0">
                          <SourcesList sources={activePiece.sources} className="mt-0 pt-0 border-t-0" />
                        </div>
                      )}
                      {activePiece?.research_summary && (
                        <div className="border-t border-indigo-100 pt-3 first:border-t-0 first:pt-0">
                          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                            Resumen de investigación
                          </h3>
                          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                            {activePiece.research_summary}
                          </p>
                          {activePiece.research_key_findings && activePiece.research_key_findings.length > 0 && (
                            <ul className="mt-2 list-disc pl-4 space-y-1 text-xs text-slate-600">
                              {activePiece.research_key_findings.map((finding, i) => (
                                <li key={i}>{finding}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  ) : null}

                  {/* Table of contents */}
                  {tocHeadings.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        En este artículo
                      </h3>
                      <ContentTableOfContents headings={tocHeadings} markdown={displayFields?.body ?? activePiece?.body ?? ''} />
                    </div>
                  )}

                  {citationRefsWhenNoSources.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        Referencias en el texto
                      </h3>
                      <ol className="list-decimal pl-4 space-y-1 text-xs text-slate-600">
                        {citationRefsWhenNoSources.map((n) => (
                          <li key={n}>
                            <a href={`#fuente-${n}`} className="text-emerald-600 hover:underline">
                              Referencia {n}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Critique Score */}
                  {latestVersion && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        Evaluación AI
                      </h3>
                      <CritiquePanel
                        score={latestVersion.critique_score}
                        feedback={latestVersion.critique_feedback}
                      />
                    </div>
                  )}

                  {/* Feedback */}
                  {activePiece && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        Feedback
                      </h3>
                      <FeedbackForm
                        contentId={activePiece.id}
                        contentBody={displayFields?.body ?? activePiece.body}
                      />
                    </div>
                  )}

                  {/* SEO Preview - fields may come from extended DB queries */}
                  <SEOPreview
                    metaTitle={(activePiece as unknown as Record<string, unknown>)?.meta_title as string | undefined}
                    metaDescription={(activePiece as unknown as Record<string, unknown>)?.meta_description as string | undefined}
                    slug={(activePiece as unknown as Record<string, unknown>)?.slug as string | undefined}
                  />

                  {/* Social Preview - fields may come from extended DB queries */}
                  <SocialPreview
                    hashtags={(activePiece as unknown as Record<string, unknown>)?.hashtags as string[] | undefined}
                    mentions={(activePiece as unknown as Record<string, unknown>)?.mentions as string[] | undefined}
                    links={(activePiece as unknown as Record<string, unknown>)?.links as Array<{ url: string; text?: string }> | undefined}
                    imagePrompts={(activePiece as unknown as Record<string, unknown>)?.image_prompts as Array<{ prompt: string; style?: string }> | undefined}
                    channel={task.channel}
                  />

                  {/* Version History */}
                  {activePiece && (activePiece.current_version || 1) >= 1 && (
                    <div>
                      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        Versiones
                      </h3>
                      <VersionCompare
                        versions={versions || []}
                        isLoading={isLoadingVersions}
                      />
                    </div>
                  )}

                  {/* Images panel */}
                  {activePiece && task && (
                    <ContentImagesPanel
                      contentId={activePiece.id}
                      contentExcerpt={
                        (displayFields?.excerpt ?? displayFields?.body ?? activePiece.excerpt ?? activePiece.body ?? '').slice(0, 1000)
                      }
                      channel={task.channel}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      ) : task.status === 'pending' || task.status === 'running' ? (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          <GenerationSteps
            taskId={task.id}
            isGenerating={task.status === 'running' || task.status === 'pending'}
            onCancel={handleCancel}
          />
        </div>
      ) : task.status === 'failed' ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full rounded-xl border border-red-100 bg-red-50 p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h3 className="text-base font-semibold text-red-800 mb-2">La generacion fallo</h3>
            {task.error_message && (
              <p className="text-sm text-red-600 mb-6 font-mono bg-red-100 rounded px-3 py-2 text-left break-words">
                {task.error_message}
              </p>
            )}
            {!task.error_message && (
              <p className="text-sm text-red-600 mb-6">
                El proceso termino inesperadamente. No se produjo ningun contenido.
              </p>
            )}
            <button
              onClick={handleRerun}
              className="btn btn-primary flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>
          </div>
        </div>
      ) : task.status === 'cancelled' ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
            <XCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-base font-semibold text-slate-700 mb-2">Tarea cancelada</h3>
            <p className="text-sm text-slate-500 mb-6">Esta tarea fue cancelada antes de completarse.</p>
            <button
              onClick={handleRerun}
              className="btn btn-secondary flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>
          </div>
        </div>
      ) : task.status === 'completed' ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-slate-500">
            <XCircle className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p>No se genero contenido para esta tarea.</p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
